import { TOPO_TIERS, TOPO_KIND_LABEL, type TopoModel, type TopoNodeKind, type TopoTierId } from "../dataImport/topology/topologyTypes";

// A flat, serialisable read of the diagram for surfaces that can't render an
// interactive SVG scene — the Word export, the API payloads, and the plain
// text summary shown under the canvas.

export type TopologyTierSummary = {
  tierId: TopoTierId;
  label: string;
  blurb: string;
  nodes: { id: string; label: string; kindLabel: string; legacy: boolean; manual: boolean }[];
  /** Human-readable links out of this tier, e.g. "Core switch → NX9K". */
  links: string[];
};

export type TopologySummary = {
  nodeCount: number;
  linkCount: number;
  legacyCount: number;
  manualCount: number;
  edited: boolean;
  tiers: TopologyTierSummary[];
  /** Kinds present, most common first — a one-line "what is this estate". */
  composition: { kind: TopoNodeKind; kindLabel: string; count: number }[];
};

export function summariseTopology(model: TopoModel): TopologySummary {
  const byId = new Map(model.nodes.map((n) => [n.id, n]));

  const tiers: TopologyTierSummary[] = TOPO_TIERS.map((tier) => {
    const nodes = model.nodes
      .filter((n) => n.tier === tier.id)
      .sort((a, b) => a.row - b.row || a.col - b.col);
    const ids = new Set(nodes.map((n) => n.id));
    const links = model.edges
      .filter((e) => ids.has(e.from))
      .map((e) => {
        const to = byId.get(e.to);
        const from = byId.get(e.from);
        return from && to ? `${from.label} → ${to.label}` : null;
      })
      .filter((s): s is string => s !== null);
    return {
      tierId: tier.id,
      label: tier.label,
      blurb: tier.blurb,
      nodes: nodes.map((n) => ({
        id: n.id,
        label: n.label,
        kindLabel: TOPO_KIND_LABEL[n.kind] ?? n.kind,
        legacy: Boolean(n.legacy),
        manual: Boolean(n.manual),
      })),
      links,
    };
  }).filter((t) => t.nodes.length > 0);

  const kindCounts = new Map<TopoNodeKind, number>();
  for (const n of model.nodes) kindCounts.set(n.kind, (kindCounts.get(n.kind) ?? 0) + 1);

  return {
    nodeCount: model.nodes.length,
    linkCount: model.edges.length,
    legacyCount: model.nodes.filter((n) => n.legacy).length,
    manualCount: model.nodes.filter((n) => n.manual).length,
    edited: Boolean(model.edited),
    tiers,
    composition: [...kindCounts.entries()]
      .map(([kind, count]) => ({ kind, kindLabel: TOPO_KIND_LABEL[kind] ?? kind, count }))
      .sort((a, b) => b.count - a.count || a.kindLabel.localeCompare(b.kindLabel)),
  };
}
